import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';



const UpdateClassificationDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  corporateAccount,
  currentProject,
  login_token,
  requirementIdWithPrefix,
  userId
}) => {
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [classificationOptions, setClassificationOptions] = useState([]);
  const [selectedClassification, setSelectedClassification] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Add ref for focus management
  const productSelectRef = useRef(null);

  // Dialog styles (updated to match other dialogs)
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    dialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      width: '700px', // Changed from 700px to match other dialogs
      maxWidth: '90vw',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      maxHeight: '90vh',
      overflowY: 'auto'
    }
  };

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && productSelectRef.current) {
      productSelectRef.current.focus();
    }
  }, [isOpen]);

  // Initialize when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Reset states when dialog opens
      setError('');
      setSelectedProductId('');
      setSelectedClassification('');
      setFormSubmitted(false);
      fetchClassificationOptions();
      fetchProducts();
    }
  }, [isOpen, corporateAccount, currentProject]);

  // Fetch valid classification options
  const fetchClassificationOptions = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: "REQUIREMENT_CLASSIFICATION",
          search_query: ""
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // Extract status values from the response
        const statuses = data.entity_statuses.map(item => item.status);
        setClassificationOptions(statuses);

        // Set default selected classification if available
        if (statuses.length > 0) {
          setSelectedClassification('');
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch classification options');
      }
    } catch (err) {
      setError(`Classification options error: ${err.message}`);
      console.error('Error fetching classification options:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch products list
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_user_level_products_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          user_corporate_account: corporateAccount,
          user_id: userId,
          corporate_account: corporateAccount,
          project_id: currentProject,
          access_level: 5 // for edit access
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const productList = data.product_list || [];
        setProducts(productList);

        // Auto-select if only one product is available
        if (productList.length === 1) {
          setSelectedProductId(productList[0].product_id);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch products');
      }
    } catch (err) {
      setError(`Products error: ${err.message}`);
      console.error('Error fetching products:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate form fields
  const validateForm = () => {
    const errors = [];

    if (selectedRequirements.size === 0) {
      errors.push('Please select at least one requirement');
    }

    if (!selectedProductId) {
      errors.push('Please select a product');
    }

    if (!selectedClassification) {
      errors.push('Please select a classification');
    }

    return errors;
  };

  // Handle classification update submission
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submit button clicked');
    setFormSubmitted(true);

    // Validate form
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      console.log('Validation errors:', validationErrors);
      setError(validationErrors.join('. '));
      return;
    }

    // Proceed with API call if validation passes
    submitClassificationUpdate();
  };

  // Actual API submission function
  const submitClassificationUpdate = async () => {
    setIsLoading(true);
    setError('');

    console.log('Updating classification with data:', {
      requirements: Array.from(selectedRequirements),
      productId: selectedProductId,
      classification: selectedClassification
    });

    try {
      const response = await fetch(appConfig.getUrl('/update_requirement_classification'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          product_id: selectedProductId,
          req_ids: Array.from(selectedRequirements),
          req_classification: selectedClassification
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        console.log('Classification updated successfully');
        setError('Classification updated successfully');

        // Delay the close to show success message
        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        // Check if there were failed requirement IDs
        if (data.failed_req_ids && data.failed_req_ids.length > 0) {
          throw new Error(`Some requirements could not be updated: ${data.failed_req_ids.join(', ')}`);
        } else {
          throw new Error(data.status_description || 'Failed to update classification');
        }
      }
    } catch (err) {
      console.error('Error in API call:', err);
      setError(`API error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel button click - explicitly pass false to indicate no success
  const handleCancel = () => {
    onClose(false);
  };

  if (!isOpen) return null;

  // Get validation state for form fields
  const getFieldValidationState = (fieldName) => {
    if (!formSubmitted) return true; // Don't show validation errors until form is submitted

    switch(fieldName) {
      case 'product':
        return !!selectedProductId;
      case 'classification':
        return !!selectedClassification;
      case 'requirements':
        return selectedRequirements.size > 0;
      default:
        return true;
    }
  };

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Update Classification
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px'
            }}>
              Update Classification for:
            </h3>
            <div style={{
              fontSize: '14px',
              marginBottom: '16px',
              color: !getFieldValidationState('requirements') ? '#dc2626' : 'inherit',
              fontWeight: !getFieldValidationState('requirements') ? 'bold' : 'normal'
            }}>
              {selectedRequirements.size === 0 ? (
                'No requirements selected'
              ) : selectedRequirements.size === 1 ? (
                `Requirement: ${requirementIdWithPrefix}`
              ) : (
                `${selectedRequirements.size} requirements selected`
              )}
            </div>
          </div>

          {/* Product Dropdown */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 'bold',
                color: !getFieldValidationState('product') ? '#dc2626' : 'inherit'
              }}>
                Product <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ position: 'relative', width: '50%' }}>
                <select
                  ref={productSelectRef}
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: !getFieldValidationState('product') ? '1px solid #dc2626' : '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                  disabled={isLoading || products.length === 0}
                >
                  <option value="">Select a product</option>
                  {products.map(product => (
                    <option key={product.product_id} value={product.product_id}>
                      {product.product_name} - {product.product_version} ({product.product_company})
                    </option>
                  ))}
                </select>
                {!getFieldValidationState('product') && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    Product selection is required
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Classification Dropdown */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 'bold',
                color: !getFieldValidationState('classification') ? '#dc2626' : 'inherit'
              }}>
                Classification <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ position: 'relative', width: '50%' }}>
                <select
                  value={selectedClassification}
                  onChange={(e) => setSelectedClassification(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: !getFieldValidationState('classification') ? '1px solid #dc2626' : '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f9fafb',
                    fontSize: '14px'
                  }}
                  disabled={isLoading || classificationOptions.length === 0}
                >
                  <option value="">Select a classification</option>
                  {classificationOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {!getFieldValidationState('classification') && (
                  <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                    Classification selection is required
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444',
              fontWeight: 'bold'
            }}>
              {error.includes('. ') ? (
                <>
                  <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Please correct the following errors:</p>
                  <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                    {error.split('. ').map((err, index) => (
                      <li key={index} style={{ marginBottom: '4px' }}>{err}</li>
                    ))}
                  </ul>
                </>
              ) : (
                error
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Classification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateClassificationDialog;